import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Users, BarChart, Globe, Settings, MessageSquare, CreditCard } from "lucide-react";
import dashboardImage from "@/assets/demo-dashboard-optimized.png";
import employeesImage from "@/assets/demo-employees.png";
import requestsImage from "@/assets/demo-requests.png";
import trackingImage from "@/assets/demo-tracking.png";
import countriesImage from "@/assets/demo-countries.png";
import pointsImage from "@/assets/demo-points.png";

const ProductDemoSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Dashboard de pilotage RH",
      description: "Vue d'ensemble temps réel de tous vos collaborateurs en télétravail international avec KPIs et alertes.",
      image: dashboardImage,
      icon: BarChart,
      features: ["Métriques en temps réel", "Alertes automatiques", "Reporting avancé"]
    },
    {
      title: "Gestion des équipes nomades",
      description: "Centralisez les informations de vos collaborateurs : localisation, statut, documents, historique des voyages.",
      image: employeesImage,
      icon: Users,
      features: ["Profils complets", "Géolocalisation", "Historique détaillé"]
    },
    {
      title: "Processus de demandes simplifié",
      description: "Workflow automatisé pour les demandes de télétravail avec validation, conformité et suivi intégré.",
      image: requestsImage,
      icon: Settings,
      features: ["Validation en 1 clic", "Conformité automatique", "Suivi temps réel"]
    },
    {
      title: "Suivi mondial des destinations",
      description: "Cartographie interactive des destinations populaires avec statistiques et recommandations personnalisées.",
      image: trackingImage,
      icon: Globe,
      features: ["Carte interactive", "Statistiques détaillées", "Recommandations IA"]
    },
    {
      title: "Analyse par pays et régions",
      description: "Analytics poussées par destination avec coûts, satisfaction et optimisations suggérées.",
      image: countriesImage,
      icon: BarChart,
      features: ["Analytics avancées", "Optimisation coûts", "Benchmarking"]
    },
    {
      title: "Système de points et récompenses",
      description: "Gamification avec système de Nest Points pour motiver et récompenser vos collaborateurs.",
      image: pointsImage,
      icon: CreditCard,
      features: ["Gamification", "Récompenses", "Motivation équipes"]
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section className="py-24 px-4 bg-background">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            <MessageSquare className="h-4 w-4 mr-2" />
            Démonstration produit
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Côté Entreprise : Pilotez vos équipes
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Une plateforme intuitive qui transforme la gestion du télétravail international. 
            Chaque fonctionnalité a été pensée pour simplifier votre quotidien RH.
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="relative bg-white rounded-2xl shadow-2xl border overflow-hidden">
                <img 
                  src={slides[currentSlide].image} 
                  alt={slides[currentSlide].title}
                  className="w-full h-auto transition-all duration-500"
                  width="1188"
                  height="862"
                />
              </div>
              
              {/* Navigation arrows */}
              <Button
                variant="outline"
                size="sm"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm"
                onClick={prevSlide}
                aria-label="Diapositive précédente"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm"
                onClick={nextSlide}
                aria-label="Diapositive suivante"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="space-y-8 order-1 lg:order-2">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    {React.createElement(slides[currentSlide].icon, { className: "h-6 w-6 text-primary" })}
                  </div>
                  <Badge variant="outline" className="text-sm">
                    {currentSlide + 1} / {slides.length}
                  </Badge>
                </div>
                
                <h3 className="text-3xl font-bold text-foreground">
                  {slides[currentSlide].title}
                </h3>
                
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {slides[currentSlide].description}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3">
                {slides[currentSlide].features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Slide indicators */}
              <div className="flex gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    aria-label={`Aller à la diapositive ${index + 1}`}
                    className={`w-12 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-primary' 
                        : 'bg-muted hover:bg-muted-foreground/20'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDemoSection;